import React, { useEffect, useState } from 'react';
import { Button, Result } from 'antd';
import Banner from '@/pages/HomePage/components/Banner';
import BigBannerShell from '@/pages/HomePage/components/BigBannerShell';

import {
  fetchHomePageConfig,
  getCachedHomePageConfig,
  getHomePageModuleList,
  preloadHomePageLcpFromConfig,
} from '@/util/homepageConfig';
import { ensureHomepageConfig } from '@/util/ensureHomepageConfig';
import { isGuest } from '@/util/commonUtils';
import HomePageSkeleton from '@/components/HomePageSkeleton';
import { renderModuleItem } from './components/HomepageModules';
import type { ModuleItem } from './components/HomepageModules';
import styles from './index.module.scss';

const findBigBannerModule = (moduleList: ModuleItem[] = []) =>
  moduleList.find(item => item.moduleType === '10100-01') || null;

const MyComponent = () => {
  const cachedConfig = getCachedHomePageConfig();
  const cachedModules = cachedConfig ? getHomePageModuleList(cachedConfig) : [];
  const hasCachedModules = Boolean(cachedConfig?.success && cachedModules.length);

  const [modules, setModules] = useState<ModuleItem[]>(hasCachedModules ? cachedModules : []);
  const [loading, setLoading] = useState(!hasCachedModules);
  const [bannerModule, setBannerModule] = useState<ModuleItem | null>(() => findBigBannerModule(cachedModules));
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (hasCachedModules) {
      preloadHomePageLcpFromConfig(getCachedHomePageConfig());
    }
  }, [hasCachedModules]);

  useEffect(() => {
    let cancelled = false;

    const initConfig = async () => {
      try {
        if (!hasCachedModules) {
          setLoading(true);
          setLoadError(null);
        }

        if (!isGuest()) {
          // CMS 请求依赖初始化后的客户配置；等待同一单飞初始化，避免首次登录用旧状态误跳列表页。
          await ensureHomepageConfig();
        }

        const result = await fetchHomePageConfig();
        if (cancelled) {
          return;
        }

        const moduleList = getHomePageModuleList(result);
        preloadHomePageLcpFromConfig(result);

        const bigBannerModule = findBigBannerModule(moduleList);
        if (bigBannerModule) {
          // CMS 返回后先出 Banner，下方楼层仍骨架，避免游客站 Logo 误当 LCP
          setBannerModule(bigBannerModule);
        }

        if (result?.success && moduleList.length) {
          setModules(moduleList);
        } else if (!hasCachedModules) {
          // CMS 缺少默认模板或初始化失败时仍保留商品浏览入口，避免首页错误阻断主流程。
          window._openPage('/page/list', '_blank');
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch homepage data:', error);
          setLoadError(TLT('首页加载失败，请刷新后重试'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void initConfig();

    return () => {
      cancelled = true;
    };
  }, [hasCachedModules]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.headBannerController}>
          {bannerModule ? <Banner data={bannerModule} type="big" /> : <BigBannerShell />}
        </div>
        <HomePageSkeleton hideBanner />
      </div>
    );
  }

  if (loadError && modules.length === 0) {
    return (
      <div className={styles.container}>
        <Result
          status="error"
          title={loadError}
          extra={
            <Button type="primary" onClick={() => window.location.reload()}>
              {TLT('刷新')}
            </Button>
          }
        />
      </div>
    );
  }

  return <div className={styles.container}>{modules.map(renderModuleItem)}</div>;
};

export default MyComponent;
